<template>
  <v-form id="location-form" @submit.prevent="onSubmit" autocomplete="off" name="lastpass-disable-search">
    <dialog-confirm ref="overlapping-locations-dialog">
      <template #title>
        {{ $t('overlapping_location_warning_1') }}
      </template>
      {{ $t('overlapping_location_warning_1') }}
      <v-list>
        <v-list-item v-for="location in overlappingLocations" :key="location.id">
          <router-link :to="`/location/${location.id}`" target="_blank">
            {{ location.label }}
            <v-icon>mdi-open-in-new</v-icon>
          </router-link>
        </v-list-item>
      </v-list>
      {{ $t('overlapping_location_warning_2') }}
    </dialog-confirm>
    <dialog-confirm ref="location-code-exists-dialog">
      <template #title>
        {{ $t('location_exists') }}
      </template>
      <div v-if="existingLocation">
        {{
          $t('location_exists_by_code_modal_prompt', {
            location_code: existingLocation.code,
            existing_customer: existingLocation.primary_customer.name,
            existing_label: existingLocation.label,
            user_customer: this.customerContext.name
          })
        }}
      </div>
    </dialog-confirm>
    <dialog-confirm ref="location-coords-exists-dialog">
      <template #title>
        {{ $t('location_exists') }}
      </template>
      <div v-if="existingLocation">
        {{
          $t('location_exists_by_coords_modal_prompt', {
            location_lat: existingLocation.latitude,
            location_long: existingLocation.longitude,
            existing_customer: existingLocation.primary_customer.name,
            existing_label: existingLocation.label,
            user_customer: this.customerContext.name
          })
        }}
      </div>
    </dialog-confirm>
    <v-row>
      <v-col lg="5" cols="12">
        <v-text-field
          v-model="localModel.label"
          :label="$t('location_name')"
          :error-messages="labelErrors"/>
        <v-text-field
          :disabled="unregistered"
          v-model="localModel.code"
          :label="$t('location_code')"
          :hint="$t('location_code_info')"
          :error-messages="codeErrors"/>
        <v-select
          v-model="localModel.flags"
          :items="locationFlagOptions"
          :hint="locationTypeHint"
          persistent-hint
          :label="$t('location_type')"/>
        <v-select
          v-model="selectedTimezone"
          :items="timeZoneOptions"
          :label="$t('location_timezone')"/>
      </v-col>
      <v-col lg="7" cols="12">
        <div>
          <gmap-map class="location-map mb-4" ref="map" :center="gmapsCoords" :zoom="defaultMapZoom">
            <div v-if="localModel.googleMapsPolygon">
              <!-- Don't let the user edit the location if there is a valid polygon. -->
              <gmap-marker :position="gmapsCoords" :draggable="false" />
              <div v-if="geofence.enabled && geofence.draw">
                <gmap-polygon :paths="localModel.googleMapsPolygon" :options="geofence.options" />
              </div>
            </div>
            <div v-else>
              <gmap-marker :position="gmapsCoords" :draggable="true" @dragend="dragEnd" />
              <div v-if="geofence.enabled && geofence.draw">
                <!-- Google maps uses meters, so convert radius from feet to meters -->
                <gmap-circle :center="gmapsCoords" :radius="gmapsRadius" :options="geofence.options"/>
              </div>
            </div>
          </gmap-map>
          <div class="d-flex justify-space-between align-center">
            <div class="d-flex align-center">
              <span>{{ $t('gps_coordinates') }}:</span>
              <v-chip small dark class="ml-2">{{ displayedLat }}, {{ displayedLng }}</v-chip>
            </div>
            <div class="d-flex justify-space-between align-center">
              <v-btn v-if="action === 'update' && (coordsChanged || radiusChanged)" text color="primary" @click="resetGeofence">
                <v-icon>mdi-arrow-u-left-top</v-icon>
                {{ $t('reset_location') }}
              </v-btn>
              <v-checkbox
                small dense
                hide-details
                class="pb-2"
                v-if="geofence.enabled"
                v-model="geofence.draw"
                :label="$t('show_geofence')"/>
            </div>
          </div>
          <div v-if="coordinateEstimate" class="text-caption mb-2">
            <v-icon small>mdi-map-marker-question</v-icon>
            {{ $t('gps_estimated_from_route', { route: coordinateEstimate.route_name, time: estimateObservedAt }) }}
          </div>
        </div>
        <v-text-field
          v-model="searchQuery"
          :label="$t('location_search')"
          :hint="$t('location_search_info')"
          :disabled="localModel.googleMapsPolygon != null"
          @keydown.enter.prevent="searchMap(searchQuery)"
          @blur="searchMap(searchQuery)"
          append-icon="mdi-magnify"
          :error-messages="gmapsCoordsErrors"
          @click:append="searchMap(searchQuery)"/>
        <v-text-field
          v-show="localModel.geofence_type === 'circle'"
          v-model="geofence.radius"
          :placeholder="customerDistance(customerDefaultRadius).toString()"
          persistent-placeholder
          :disabled="localModel.geofence_type === 'polygon'"
          :label="$t('geofence_radius')"
          :error-messages="geofenceRadiusErrors"
          :suffix="$tc(radiusUnitOfMeasure, 2)"/>
        <div v-if="localModel.googleMapsPolygon" class="text-subtitle-2 mb-4 warning--text">
          <v-icon small color="warning">mdi-alert</v-icon>
          {{ $t('editing_polygon_locations_disabled') }}
        </div>
      </v-col>
    </v-row>
    <div class="d-flex justify-end">
      <v-btn
        v-if="action === 'create' && user.can('create_location')"
        type="submit"
        color="primary"
        :loading="loading">
        <v-icon>mdi-plus</v-icon>
        {{ $t('create') }}
      </v-btn>
      <v-btn
        v-else-if="showBtn('update_location')"
        type="submit"
        color="primary"
        :loading="loading"
        @click="$gtag.event(action, {event_category: 'Location'})">
        <v-icon>mdi-content-save</v-icon>
        {{ $t('save_changes') }}
      </v-btn>
    </div>
    <v-divider class="my-4"/>
  </v-form>
</template>

<script>
import DialogConfirm from '@/components/shared/DialogConfirm'
import LocationUtilitiesMixin from '@/components/location/LocationUtilitiesMixin'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import { required, decimal } from 'vuelidate/lib/validators'
import { get, map, cloneDeep, sortBy, defaultTo, filter, head, isArray, intersection, floor } from 'lodash'
import { gmapApi } from 'vue2-google-maps'
import Moment from 'moment-timezone'
import LocationRecord from '@/orm/models/LocationRecord'

export default {
  name: 'LocationForm.vue',
  components: {
    DialogConfirm
  },
  mixins: [
    CurrentUserMixin,
    LocationUtilitiesMixin,
    ValidationMixin
  ],
  props: {
    model: Object
  },
  validations: {
    localModel: {
      label: {
        required
      },
      code: {
        required
      }
    },
    geofence: {
      radius: {
        // require a decimal number
        decimal,
        // If the radius is set require that it be between the min and max limits
        metricBetween: function (value) {
          return value == null || (this.metricDistance(value) >= LocationRecord.minRadius && this.metricDistance(value) <= LocationRecord.maxRadius)
        }
      }
    },
    gmapsCoords: {
      validPosition: function (value) {
        return value.lat !== 0 && value.lng !== 0
      }
    }
  },
  data () {
    // When creating a new model, set default values for keys we want to ensure are reactive in Vue.
    const defaultObj = {
      latitude: 0,
      longitude: 0,
      flags: 0,
      meta: {
        RoutePlanTZ: 'UTC'
      }
    }
    return {
      localModel: cloneDeep(this.model) || this.$orm('Location').createRecord(defaultObj),
      gmapsSearchFailure: false,
      unregistered: false,
      geofence: {
        enabled: false,
        draw: true,
        radius: 0,
        options: {
          fillColor: '#d9534f',
          fillOpacity: 0.2,
          strokeColor: '#d9534f'
        }
      },
      // decent default zoom level that's at about city level in most metros
      defaultMapZoom: 12,
      searchQuery: '',
      locationTypes: [],
      timeZoneOptions: [],
      // local reference to google map
      map: undefined,
      overlappingLocations: [],
      rules: {
        required: value => !!value || 'Required.'
      },
      loading: false,
      // Temp value to use in overlap modals for existing location
      existingLocation: null,
      // GPS suggestion derived from vehicle telemetry for unregistered codes
      coordinateEstimate: null
    }
  },
  computed: {
    /**
     * Get the current action (so we can handle update/create differently)
     * @return {string}
     */
    action () {
      return this.localModel.id == null ? 'create' : 'update'
    },
    /**
     * The coordinate estimate's observation time, in the customer's timezone.
     * @return {string}
     */
    estimateObservedAt () {
      if (!this.coordinateEstimate) {
        return ''
      }
      return Moment(this.coordinateEstimate.observed_at).tz(this.customerContext.timezone()).format('lll')
    },
    /**
     * Expose imported gmapApi symbol as 'google' so we can use 'google.maps' object inside Vue as we would normally in
     * any other Javascript project.
     * (As recommended by the vue-google-maps documentation)
     */
    google: gmapApi,
    /**
     * Computed value to get coordinates in the form Google Maps expects
     * @return {Object}
     */
    gmapsCoords () {
      const lat = defaultTo(this.localModel.getLatitude(), 0)
      const lng = defaultTo(this.localModel.getLongitude(), 0)
      return { lat: lat, lng: lng }
    },
    displayedLat () {
      return floor(this.gmapsCoords.lat, 6)
    },
    displayedLng () {
      return floor(this.gmapsCoords.lng, 6)
    },
    /**
     * When rendering the radius on Google Maps show either the location's explicit value or the customer default.
     * @return {float}
     */
    gmapsRadius () {
      // The metricDistance function below assumes the value we're getting is in the customer distance scale
      // (imperial/metric), however the customer default radius is always in metric, so if we use the default radius
      // convert it to the customer's scale first.
      const radius = this.geofence.radius || this.customerDistance(this.customerDefaultRadius, 6)
      return this.metricDistance(radius, 6)
    },
    /**
     * Return a boolean flag indicating if the value of the lat/long coordinates are different from the values in
     * localModel.
     * @returns {boolean}
     */
    coordsChanged () {
      return (this.model.latitude !== this.localModel.latitude) ||
             (this.model.longitude !== this.localModel.longitude)
    },
    /**
     * Return a boolean flag indicating if the value of the radius on our geofence object is different from the value in
     * the original LocationRecord
     * @return {boolean}
     */
    radiusChanged () {
      return this.geofence.radius !== this.customerDistance(this.localModel.getRadius(null))
    },
    /**
     * Get the options for the location type picker.
     * @return {Object}
     */
    locationFlagOptions () {
      if (this.user.can('create_location', 'all')) {
        return map(this.locationTypes, (locationType) => {
          return {
            text: this.$i18n.tc(`${locationType.code}_location_type`),
            value: locationType.id
          }
        })
      } else {
        const filteredLocationTypes = filter(this.locationTypes, (locationType) => {
          return locationType.code !== 'dc'
        })
        return map(filteredLocationTypes, (locationType) => {
          return {
            text: this.$i18n.tc(`${locationType.code}_location_type`),
            value: locationType.id
          }
        })
      }
    },
    /**
     * Special computed value to act as a handler/delegate for time zone picking.
     */
    selectedTimezone: {
      get: function () {
        const timezoneVal = get(this.localModel, 'meta.RoutePlanTZ', 'UTC')
        const zone = Moment.tz(timezoneVal)
        return {
          value: timezoneVal,
          offset: zone.utcOffset(),
          label: `(UTC${zone.format('Z')}) ${timezoneVal}`
        }
      },
      set: function (value) {
        this.localModel.meta.RoutePlanTZ = value.value
      }
    },
    /**
     * Return a hint depending on the location type selected (Just returning only ignored for now)
     */
    locationTypeHint () {
      switch (this.localModel.flags) {
        case 8:
          return this.$t('location_ignored_type_info')
      }
      return undefined
    },
    /**
     * Errors for the location label text field.
     * @returns {*}
     */
    labelErrors () {
      return this.validationError({
        prop: this.$v.localModel.label,
        message: this.$t('required')
      })
    },
    /**
     * Errors for the location code text field.
     * @returns {*}
     */
    codeErrors () {
      return this.validationError({
        prop: this.$v.localModel.code,
        message: this.$t('required')
      })
    },
    /**
     * Errors for the location coordinates/search field.
     * @returns {*[]}
     */
    gmapsCoordsErrors () {
      const errors = []
      if (this.gmapsSearchFailure) {
        errors.push(this.$t('location_not_found_try_again'))
      }
      return errors.concat(this.validationError({
        prop: this.$v.gmapsCoords,
        message: this.$t('pick_a_location_on_the_map')
      }))
    },
    /**
     * Errors for the geofence radius text field.
     * @returns {*}
     */
    geofenceRadiusErrors () {
      return this.validationError({
        prop: this.$v.geofence.radius,
        message: this.$t('geofence_radius_size_warning', {
          min: this.customerDistance(LocationRecord.minRadius),
          max: this.customerDistance(LocationRecord.maxRadius),
          unit: this.$tc(this.radiusUnitOfMeasure, 2)
        })
      })
    }
  },
  methods: {
    /**
     * Determine based on user permissions and location type if the button should be shown.
     * @param {string} btnType
     */
    showBtn (btnType) {
      if (this.user.can(btnType, 'all')) {
        return true
      } else if ((this.localModel.isDeliveryLocation() || this.localModel.isIgnored()) && this.user.can(btnType, 'delivery')) {
        // Mirror the backend's LocationController::checkAccess(), which grants the 'delivery' scope access to both
        // delivery and ignored locations. Without the isIgnored() check the save button is hidden for admins editing
        // ignored locations even though the API would accept the update.
        return true
      }
      return false
    },
    /**
     * Reset the geofence back to the original coordinates/radius.
     * @return void
     */
    resetGeofence () {
      this.searchQuery = ''
      this.geofence.radius = this.customerDistance(this.localModel.getRadius(null))
      this.updateMapPosition(this.model.latitude, this.model.longitude)
    },
    /**
     * Helper function for the save process. Check related customers by code to see if this location already exists.
     * If code exists on the same customer, stop the save and show an error.
     * If code exists on another customer in the same family, stop the save, but give the option to associate the
     * location to the current customer context.
     * @returns {Promise<boolean>} Returns true if the save operation should proceed, false if not.
     */
    async checkExistingByCode () {
      const matchingLocations = await this.$orm('Location').findAll({
        // Include all sibling and parent customers the current customer context
        customer_id: map(this.relatedCustomers, c => c.id).join(','),
        code: this.localModel.code,
        expand: 'primary_customer'
      })
      if (matchingLocations.getTotalItemCount() > 0) {
        // The API will return partial matches, so filter those out.
        const location = head(filter(matchingLocations.getItems(), (location) => {
          return location.code === this.localModel.code
        }))
        if (location) {
          if (location.primary_customer.id === this.customerContext.id) {
            // Location already exists on this customer, show an error banner
            await this.$store.dispatch('globalMessagingModule/showErrorFlashBanner',
              this.$t('location_exists_by_code_error', {
                location_code: location.code,
                existing_label: location.label
              }))
          } else {
            this.existingLocation = location
            // Location exists on another customer, give option to associate, otherwise don't do anything.
            if (await this.$refs['location-code-exists-dialog'].confirm()) {
              this.linkExistingLocationToCustomer(location)
            }
          }
          // Don't proceed with save
          return false
        }
      }
      // Proceed with save
      return true
    },
    /**
     * Helper function for the save process. Check related customers by coords to see if this location already exists.
     * If exact coordinates exist on the same customer, show an error.
     * If coordinates exist on another customer in the same family, give the option to associate the location to the
     * current user.
     * @returns {Promise<boolean>} Returns true if the save operation should proceed, false if not.
     */
    async checkExistingByCoords () {
      const matchingLocations = await this.$orm('Location').findAll({
        // Include all sibling and parent customers in the current customer context.
        customer_id: map(this.relatedCustomers, c => c.id).join(','),
        latitude: this.localModel.latitude,
        longitude: this.localModel.longitude,
        expand: 'primary_customer'
      })
      if (matchingLocations.getTotalItemCount() > 0) {
        const location = matchingLocations.getItems()[0]
        if (location.primary_customer.id === this.customerContext.id) {
          // Location exists on this customer, show an error banner
          await this.$store.dispatch('globalMessagingModule/showErrorFlashBanner',
            this.$t('location_exists_by_coords_error', {
              location_lat: location.latitude,
              location_long: location.longitude,
              existing_label: location.label
            })
          )
        } else {
          this.existingLocation = location
          // Location exists on another customer, give option to associate, otherwise don't do anything.
          if (await this.$refs['location-coords-exists-dialog'].confirm()) {
            this.linkExistingLocationToCustomer(location)
          }
        }
        // Don't proceed with save
        return false
      }
      // Proceed with save
      return true
    },
    /**
     * Check if any of the customers associated with this location have locations with a geofence that overlaps the
     * geofence set for this location. If so prompt the user to confirm they want to create the overlapping location.
     * @returns {Promise<boolean>} true if the operation will proceed, false if it will be cancelled.
     */
    async checkOverlappingLocations () {
      let customerIds = this.customerContext.id
      // If we're updating an existing record, then set the customer id string to all the currently assigned customers,
      // otherwise leave it to the current customer context.
      if (isArray(this.localModel.customers)) {
        const locationCustomerIds = map(this.localModel.customers, (c) => { return c.id })
        // Omit customers which are not in the current users scope.
        const userCustomerIds = map(this.customers, (c) => { return c.id })
        customerIds = intersection(locationCustomerIds, userCustomerIds).join(',')
      }
      // Build a request to find all locations which intersect with this based on the location's geofence and customers.
      const requestParams = {
        intersecting: 1,
        customer_id: customerIds,
        latitude: this.localModel.latitude,
        longitude: this.localModel.longitude,
        expand: 'primary_customer'
      }
      if (this.localModel.geofence) {
        requestParams.geofence = JSON.stringify(this.localModel.geofence)
      }
      if (this.localModel.id) {
        requestParams.location_id = this.localModel.id
      }
      // Look up the overlapping locations.
      const overlappingLocations = await this.$orm('Location').findAll(requestParams)
      if (overlappingLocations.getTotalItemCount() === 0) {
        return true
      }
      // There is a special case that if:
      // 1) there is only one overlapping location
      // 2) the coordinates match exactly
      // 3) the primary customer of the overlapping location isn't the current customer context, then we allow this
      // through and then offer to link the location (performed elsewhere, in checkExistingByCoords)
      if (overlappingLocations.getTotalItemCount() === 1) {
        const overlappingLocation = overlappingLocations.getItems()[0]
        if (overlappingLocation.getLatitude() === this.localModel.latitude &&
          overlappingLocation.getLongitude() === this.localModel.longitude) {
          return true
        }
      }
      // If we've made it here, we need to show the overlapping locations dialog.
      this.overlappingLocations = overlappingLocations.getItems()
      // Return the user's choice in the dialog.
      return await this.$refs['overlapping-locations-dialog'].confirm()
    },

    /**
     * Submit the form to the API.
     * @return {boolean} if the submission was successful
     */
    async onSubmit () {
      this.$v.$touch()
      if (this.$v.$invalid) {
        return false
      }
      this.loading = true
      // If the radius has been changed then set it on the model
      if (this.geofence.radius != null) {
        this.localModel.setRadius(this.metricDistance(this.geofence.radius, 6))
      }
      try {
        if (await this.checkOverlappingLocations()) {
          if (this.action === 'create') {
            await this.create()
          } else {
            await this.update()
          }
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.loading = false
      }
    },
    /**
     * Create a new location
     */
    async create () {
      this.localModel.customers = [this.customerContext.id]
      const codeCheck = await this.checkExistingByCode()
      const coordsCheck = await this.checkExistingByCoords()
      if (codeCheck && coordsCheck) {
        await this.$orm('Location').createOrUpdate(this.localModel)
        this.$router.push('/locations', () => {
          this.$store.dispatch(
            'globalMessagingModule/showSuccessFlashBanner',
            `${this.$tc('location')} ${this.localModel.label} ${this.$t('generic_save_success')}`
          )
        })
      }
    },
    /**
     * Update the existing location
     */
    async update () {
      await this.$orm('Location').createOrUpdate(this.localModel)
      this.$router.push('/locations', () => {
        this.$store.dispatch(
          'globalMessagingModule/showSuccessFlashBanner',
          `${this.$tc('location')} ${this.localModel.label} ${this.$t('generic_save_success')}`
        )
      })
    },
    /**
     * Perform a geocoding lookup to Google Maps API.
     * The marker will be updated to the returned value, and the zoom updated to the returned bounding box.
     * If given a lat/long pair instead of a query, the marker will be set to those coordinates, and the default zoom
     * used instead.
     * @param query
     * @return void
     */
    searchMap (query) {
      this.gmapsSearchFailure = false
      if (query === '') {
        return
      }
      // Try to look up by coordinates first. If we can't parse the coordinates, try a geocode lookup
      const coordinates = query.split(',')
      if (coordinates.length === 2) {
        const lat = parseFloat(coordinates[0])
        const lng = parseFloat(coordinates[1])
        if (parseFloat(lat) >= -90 && parseFloat(lat) <= 90 && parseFloat(lng) >= -180 && parseFloat(lng) <= 180) {
          this.updateMapPosition(lat, lng)
          this.map.setZoom(this.defaultMapZoom)
          return
        }
      }
      // Not a lat/long pair, do a geocode lookup instead
      new this.google.maps.Geocoder().geocode({
        address: query
      },
      (results, status) => {
        if (status === 'OK') {
          const location = get(results, '[0].geometry.location')
          const bounds = get(results, '[0].geometry.bounds')
          if (location) {
            this.updateMapPosition(location.lat(), location.lng())
          }
          if (bounds) {
            this.map.fitBounds(bounds)
          } else {
            this.map.setZoom(this.defaultMapZoom)
          }
        } else {
          this.gmapsSearchFailure = true
        }
      })
    },
    /**
     * Event handler for dragging the google maps marker.
     * @param event
     * @return void
     */
    dragEnd (event) {
      this.updateMapPosition(event.latLng.lat(), event.latLng.lng())
    },
    /**
     * Update the model based on the specified coordinates.
     * @param latitude
     * @param longitude
     * @return void
     */
    updateMapPosition (latitude, longitude) {
      this.gmapsSearchFailure = false
      this.localModel.latitude = latitude
      this.localModel.longitude = longitude
    },
    /**
     * For unregistered codes, ask the API where the vehicle dwelled around the
     * planned ETA and drop the pin there instead of at 0,0. Best-effort: any
     * failure just leaves the default map position in place.
     * @return void
     */
    async fetchCoordinateEstimate () {
      try {
        const response = await this.$orm('UnregisteredLocationCoordinates').findAll({
          code: this.localModel.code,
          customer_id: this.customerContext.id
        })
        const estimate = response.getItems()[0]
        // Only apply if the user hasn't already positioned the pin themselves
        if (estimate && this.localModel.latitude === 0 && this.localModel.longitude === 0) {
          this.coordinateEstimate = estimate
          this.updateMapPosition(estimate.latitude, estimate.longitude)
          if (this.map) {
            this.map.setZoom(16)
          }
        }
      } catch (error) {
        // Suggestion only; never block the form over it
      }
    },
    /**
     * Link an existing location to the current customer.
     * @param existingLocation
     * @return void
     */
    async linkExistingLocationToCustomer (existingLocation) {
      const record = this.$orm('Location').createRecord(existingLocation)
      try {
        await record.linkCustomer(this.customerContext.id)
        this.$router.push('/locations', () => {
          this.$store.dispatch(
            'globalMessagingModule/showSuccessFlashBanner',
            this.$t('location_assigned_to_customer', {
              location: record.label,
              customer: this.customerContext.name
            })
          )
        })
      } catch (error) {
        this.handleError(error)
      }
    }
  },
  watch: {
    customerContext: function (newValue) {
      // update geofence options
      this.geofence.enabled = get(newValue.getSetting('processing.GpsLocationState'), 'enabled')
    },
    // Watch for changes to the location model passed in as a prop. In particular we want to make sure our customer list
    // is up-to-date so that when we call checkOverlappingLocations() it has the correct list of customers to check
    // against.
    model: {
      handler (newLocation) {
        this.localModel.customers = newLocation.customers
        this.localModel.customer = newLocation.customer
        this.localModel.primary_customer = newLocation.primary_customer
      },
      deep: true
    }
  },
  created () {
    if (this.$route.query.unregistered && this.$route.query.code) {
      this.unregistered = true
      this.localModel.code = this.$route.query.code
    }
    // Get all the time zone options from Moment and put them into objects that v-select can use.
    this.timeZoneOptions = map(Moment.tz.names(), (name) => {
      const zone = Moment.tz(name)
      return {
        value: name,
        offset: zone.utcOffset(),
        text: `(UTC${zone.format('Z')}) ${name}`
      }
    })
    this.timeZoneOptions = sortBy(this.timeZoneOptions, ['offset', 'value'])

    // Initialize geofence display options
    this.geofence.enabled = get(this.customerContext.getSetting('processing.GpsLocationState'), 'enabled')
    this.geofence.radius = this.customerDistance(this.localModel.getRadius(null))
  },
  mounted () {
    // Mount the Google Maps as a local reference we can use elsewhere
    this.$refs.map.$mapPromise.then((map) => {
      this.map = map
      // Init map if in create mode
      if (this.action === 'create') {
        // Since 0,0 is well off the coast of Africa, zoom out a bit
        this.updateMapPosition(0, 0)
        this.map.setZoom(4)
        // Suggest the spot where the vehicle actually stopped
        if (this.unregistered) {
          this.fetchCoordinateEstimate()
        }
      }
    })
    // Pull down all the location types from the API
    this.$orm('LocationType')
      .findAll({})
      .then((collection) => {
        this.locationTypes = collection.getItems()
      })
      .catch(error => {
        this.handleError(error)
      })
  }
}
</script>

<style scoped lang="scss">
.location-map {
  height: 400px;
}
// The line height on v-text-field hints is a bit too small for me when the line breaks.
::v-deep(.v-messages__message) {
  line-height: 15px;
  margin-bottom: 15px;
}
</style>
